import { useCallback, useState } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';
import { FixedRule } from 'repository/timekeeper/@types/FixedRuleModel';
import { WorkRule } from 'repository/timekeeper/@types/WorkRuleModel';
import { HolidayRule } from 'components/dialog/pay_rules/common';
import { InterpretationRule } from 'repository/timekeeper/@types/InterpretationRuleModel';
import { HolidayTabs } from 'repository/timekeeper/@types/HolidayModel';

const timekeeperService = new TimekeeperService();

export const usePayRulesQuery = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<PayRuleDB[]>([]);
    const [fixedRules, setFixedRules] = useState<FixedRule[]>([]);
    const [interpretationRules, setInterpretationRules] = useState<InterpretationRule[]>([]);
    const [workRules, setWorkRules] = useState<WorkRule[]>([]);
    const [creditHolidayRules, setCreditHolidayRules] = useState<HolidayRule[]>([]);
    const [holidays, setHolidays] = useState<HolidayTabs[]>([]);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [payRules, fixedRulesData, interpretationRulesData, workRulesData, creditHolidayRulesData, holidaysData] = await Promise.allSettled([
                timekeeperService.payrules.getPayRules(),
                timekeeperService.fixed_rule.getFixedRules(),
                timekeeperService.interpretation_rules.getAll(),
                timekeeperService.work_rules.getWorkRules(),
                timekeeperService.credit_holiday_rule.getCreditHolidayRules(),
                timekeeperService.holiday.getHolidays(),
            ]);

            if (payRules.status === 'fulfilled') {
                const orderedData = payRules.value.sort((a: PayRuleDB, b: PayRuleDB) => a.name.localeCompare(b.name));
                setRows(orderedData || []);
            }

            if (fixedRulesData.status === 'fulfilled') {
                const orderedData = fixedRulesData.value.sort((a: FixedRule, b: FixedRule) => a.name.localeCompare(b.name));
                setFixedRules(orderedData || []);
            }
            if (interpretationRulesData.status === 'fulfilled') {
                const orderedData = interpretationRulesData.value.sort((a: InterpretationRule, b: InterpretationRule) => a.name.localeCompare(b.name));
                setInterpretationRules(orderedData || []);
            }
            if (workRulesData.status === 'fulfilled') {
                const orderedData = workRulesData.value.sort((a: WorkRule, b: WorkRule) => a.name.localeCompare(b.name));
                console.log('Fetched work rules:', orderedData);
                setWorkRules(orderedData || []);
            }
            if (creditHolidayRulesData.status === 'fulfilled') {
                const orderedData = creditHolidayRulesData.value.sort((a: HolidayRule, b: HolidayRule) => a.name.localeCompare(b.name));
                setCreditHolidayRules(orderedData || []);
            }
            if (holidaysData.status === 'fulfilled') {
                const holidayRows: HolidayTabs[] = holidaysData.value.map((holiday: { holiday_id: number; name: string; country?: string; country_name?: string }) => ({
                    id: holiday.holiday_id,
                    name: holiday.name,
                    option: false,
                    holiday_credit_rule: '',
                    country: holiday.country || holiday.country_name || '',
                }));
                const orderedData = holidayRows.sort((a, b) => a.name.localeCompare(b.name));
                setHolidays(orderedData || []);
            }
        } catch (error) {
            console.error('Error loading pay rules:', error);
            setLoading(false);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, setRows, rows, setLoading, loadData, fixedRules, interpretationRules, workRules, creditHolidayRules, holidays };
};
